import { keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'; //-tech-debt-react-query;

import { fetchItems, getApiHeaders } from '../support/queries';

export type APIClientOwner = {
  id: number;
  email?: string | null;
} | null;

export type APIClientRecord = {
  client_id: string;
  name: string;
  description?: string | null;
  enabled: boolean;

  secret_name?: string | null;
  secret_region?: string | null;
  secret_key?: string | null;
  has_secret?: boolean;

  owner?: APIClientOwner;
  owner_id?: number | null;

  token_ttl_seconds: number;
  metadata?: Record<string, any>;

  created?: string | null;
  last_updated?: string | null;
  last_used_at?: string | null;
  updated_by?: string | null;

  role_ids?: number[];
};

export type APIClientCreatePayload = {
  name: string;
  description?: string | null;
  role_ids?: number[];
  token_ttl_seconds?: number;
};

export type APIClientUpdatePayload = {
  client_id: string;
  name?: string;
  description?: string | null;
  enabled?: boolean;
  role_ids?: number[];
  token_ttl_seconds?: number;
};

export type APIClientCreateOrRotateResponse = APIClientRecord & {
  client_secret?: string;
  client_secret_returned_once?: boolean;
};

export type APIClientDeleteResponse = APIClientRecord & {
  deleted?: boolean;
};

export type APIClientRoleOption = {
  id: number;
  name: string;
  description?: string | null;
};

export const apiClientKeys = {
  all: ['api-clients'] as const,
  list: ['api-clients', 'list'] as const,
  detail: (clientId?: string | null) =>
    ['api-clients', 'detail', clientId || null] as const,
  roles: ['api-clients', 'roles'] as const,
};

function boolEnv(value?: string | null) {
  return String(value || '').trim().toLowerCase() === 'true';
}

function authApiBase() {
  if (boolEnv(process.env.REACT_APP_USE_LOCAL_AUTH_API)) {
    return (
      process.env.REACT_APP_LOCAL_AUTH_API_BASE ||
      'http://127.0.0.1:8000/api/auth'
    ).replace(/\/+$/, '');
  }

  return `${String(process.env.REACT_APP_API || '').replace(/\/+$/, '')}/auth`;
}

function normalizeEndpoint(endpoint: string) {
  return String(endpoint || '').replace(/^\/+/, '');
}

function buildAuthUrl(endpoint: string, params: Record<string, any> = {}) {
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  const query = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });

  const qs = query.toString();

  return `${authApiBase()}/${normalizedEndpoint}${qs ? `?${qs}` : ''}`;
}

async function authJsonRequest<T = any>(
  endpoint: string,
  {
    method = 'GET',
    body,
    params,
  }: {
    method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
    body?: any;
    params?: Record<string, any>;
  } = {},
): Promise<T> {
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  const authEndpointForHeader = `auth/${normalizedEndpoint}`;

  const response = await fetch(buildAuthUrl(normalizedEndpoint, params), {
    method,
    headers: getApiHeaders(null, method, authEndpointForHeader),
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const rawText = await response.text();

  let parsed: any = null;

  try {
    parsed = rawText ? JSON.parse(rawText) : null;
  } catch {
    parsed = rawText;
  }

  if (!response.ok) {
    const message =
      parsed?.error ||
      parsed?.detail ||
      parsed?.message ||
      rawText ||
      `${method} ${endpoint} failed`;

    throw new Error(message);
  }

  return parsed as T;
}

function toArray<T = any>(value: any): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (Array.isArray(value.items)) return value.items;
  if (Array.isArray(value.results)) return value.results;
  if (Array.isArray(value.records)) return value.records;
  return [];
}

export const useFetchApiClients = (
  options?: Omit<UseQueryOptions<APIClientRecord[], Error>, 'queryKey' | 'queryFn'>,
) =>
  useQuery<APIClientRecord[], Error>({
    queryKey: apiClientKeys.list,
    queryFn: async () => {
      const response = await authJsonRequest<{ items?: APIClientRecord[] }>('client/');
      return Array.isArray(response?.items) ? response.items : [];
    },
    ...{
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
      },
  });

export const useFetchApiClientDetail = (
  clientId?: string | null,
  options?: Omit<UseQueryOptions<APIClientRecord, Error>, 'queryKey' | 'queryFn'>,
) =>
  useQuery<APIClientRecord, Error>({
    queryKey: apiClientKeys.detail(clientId),
    queryFn: () =>
      authJsonRequest<APIClientRecord>(
        `client/${encodeURIComponent(String(clientId))}/`,
      ),
    ...{
        enabled: !!clientId,
        refetchOnWindowFocus: false,
        ...(options || {}),
      },
  });

export const useFetchApiClientRoleOptions = (
  options?: Omit<UseQueryOptions<APIClientRoleOption[], Error>, 'queryKey' | 'queryFn'>,
) =>
  useQuery<APIClientRoleOption[], Error>({
    queryKey: apiClientKeys.roles,
    queryFn: async () => {
      const response = await fetchItems(
        'db/role',
        {
          page: 1,
          size: 1000,
          detailed: true,
          'order-by': 'name',
        },
        {
          pagination: true,
        },
      );

      return toArray<APIClientRoleOption>(response)
        .map((role: any) => ({
          id: Number(role.id),
          name: String(role.name || role.id),
          description: role.description || null,
        }))
        .filter((role) => Number.isFinite(role.id));
    },
    ...{
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        ...(options || {}),
      },
  });

export const useCreateApiClientMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<APIClientCreateOrRotateResponse, Error, APIClientCreatePayload>({
    mutationFn: (payload) =>
      authJsonRequest<APIClientCreateOrRotateResponse>('client/create/', {
        method: 'POST',
        body: payload,
      }),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: apiClientKeys.all });

      if (data?.client_id) {
        queryClient.setQueryData(apiClientKeys.detail(data.client_id), data);
      }
    },
  });
};

export const useUpdateApiClientMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<APIClientRecord, Error, APIClientUpdatePayload>({
    mutationFn: ({ client_id, ...payload }) => {
      if (!client_id) {
        throw new Error('client_id is required');
      }

      return authJsonRequest<APIClientRecord>(`client/${encodeURIComponent(String(client_id))}/`, {
        method: 'PATCH',
        body: payload,
      });
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: apiClientKeys.all });

      if (data?.client_id) {
        queryClient.setQueryData(apiClientKeys.detail(data.client_id), data);
      }
    },
  });
};

export const useRotateApiClientSecretMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<APIClientCreateOrRotateResponse, Error, string>({
    mutationFn: (clientId) => {
      if (!clientId) {
        throw new Error('client_id is required');
      }

      return authJsonRequest<APIClientCreateOrRotateResponse>(
        `client/${encodeURIComponent(String(clientId))}/rotate-secret/`,
        {
          method: 'POST',
          body: {},
        }
      );
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: apiClientKeys.all });

      if (data?.client_id) {
        queryClient.setQueryData(apiClientKeys.detail(data.client_id), data);
      }
    },
  });
};

export const useDeleteApiClientMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<APIClientDeleteResponse, Error, string>({
    mutationFn: (clientId) => {
      if (!clientId) {
        throw new Error('client_id is required');
      }

      return authJsonRequest<APIClientDeleteResponse>(
        `client/${encodeURIComponent(String(clientId))}/`,
        {
          method: 'DELETE',
        }
      );
    },
    onSuccess: async (_data, clientId) => {
      await queryClient.invalidateQueries({ queryKey: apiClientKeys.all });
      queryClient.removeQueries({ queryKey: apiClientKeys.detail(clientId) });
    },
  });
};